import * as z from 'zod/v4';

import { dateTimeSchema } from '../frameworks/entry.js';
import { type EventId, eventIdSchema } from './EventId.js';
import { EVENT_TYPE_ID_VARIANTS, type EventType, eventTypeEnum, eventTypeIdEnum } from './EventType.js';
import type { ParticipantId } from './Participant.js';

export type EventStatisticsKey = string & { __type: 'EventStatistics' };

/**
 * Creates an key used for eventStatistics. Participants are only used in the key if eventType differs from default.
 * If participantsIds is empty key defaults to using eventType.EVENT_1X2.
 *
 * @param root0 Object with eventId, eventType, participantIds and periodNumber
 * @param root0.eventId Match event id
 * @param root0.eventType Bet event type
 * @param root0.participantIds Array of participant ids
 * @param root0.periodNumber Period number, used for Hockey in Powerplay
 * @returns string
 */
export function createEventStatisticsKey({
  eventId,
  eventType,
  participantIds,
  periodNumber,
}: {
  eventId: EventId;
  eventType: EventType;
  participantIds: ParticipantId[];
  periodNumber?: number;
}): EventStatisticsKey {
  let eventStatisticsKey = `${eventId}_${eventType}`;

  if (participantIds.length > 0) {
    eventStatisticsKey += `_${participantIds.join('_')}`;
  }

  if (periodNumber && periodNumber > 0) {
    eventStatisticsKey += `_P${periodNumber}`;
  }

  return eventStatisticsKey as EventStatisticsKey;
}

export const eventStatisticsKeySchema = z.custom<EventStatisticsKey>((value) => {
  if (typeof value !== 'string') {
    return false;
  }

  const [eventId, eventType] = value.split('_');

  if (!eventId || !eventType) {
    return false;
  }

  try {
    const coercedEventId = z.coerce.number().parse(eventId);
    eventIdSchema.parse(coercedEventId);
    eventTypeEnum.parse(eventType);
  } catch {
    return false;
  }

  return true;
});

const BET_METRIC_TYPES = ['odds', 'percentage', 'count'] as const;
const betMetricTypeEnum = z.enum(BET_METRIC_TYPES);

/**
 * TS BetMetricsType type
 */
export type BetMetricsType = z.infer<typeof betMetricTypeEnum>;

const metricsObject = z.object({
  current: z.object({
    value: z.array(z.string().nullish()),
  }),
  valueType: betMetricTypeEnum,
});

export type MetricsObject = z.infer<typeof metricsObject>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const betMetricsSchema = z.object({
  odds: metricsObject,
  favouriteOdds: metricsObject,
  startOdds: metricsObject,
  distribution: z.object({
    current: z.object({
      value: z.array(z.string().nullish()),
    }),
    previous: z
      .object({
        value: z.array(z.string().nullish()),
      })
      .optional(),
    valueType: betMetricTypeEnum,
  }),
  tioTidningarTips: metricsObject.optional(),
});

/**
 * TS BetMetrics type
 */
export type BetMetrics = z.infer<typeof betMetricsSchema>;

/**
 * TS EventTypeStatistic type
 */
export type EventTypeStatistic = BetMetrics & { id: EventStatisticsKey; eventType: EventType };

/**
 * Function to get the number of outcomes for a given event type.
 *
 * @param eventType type for event to get outcome count for
 * @returns the number of outcomes for the event type
 */
export function getOutcomeCount(eventType: EventType): 2 | 3 | 8 | 11 {
  if (eventType === 'GOAL_COUNT') {
    return 8; // should we have support for hockey here?
  }
  if (eventType === 'RESULT') {
    return 11;
  }
  if (eventType === 'OVER_UNDER') {
    return 2;
  }
  return 3;
}

/**
 * Formats a value to a string.
 * Replaces comma (,) with dot (.).
 *
 * @param value Value to format
 * @returns Returns the formatted value
 */
export function formatValue(value: string | number): string | undefined {
  return value ? `${value}`.replace(',', '.') : undefined;
}

/**
 * Zod schema for BetMetrics entity
 * Used to parse the response from the API into a BetMetrics entity
 */
export const betMetricsTransformSchema = z
  .object({
    values: z.array(
      z
        .object({
          distribution: z
            .object({
              distribution: z.string().nullish(),
              refDistribution: z.string().nullish(),
            })
            .nullish(),
          odds: z
            .object({
              favouriteOdds: z.string().nullish(),
              odds: z.string().nullish(),
              startOdds: z.string().nullish(),
            })
            .nullish(),
          outcome: z.string().optional(),
        })
        .partial()
    ),
    distributionDate: dateTimeSchema.nullish(),
    distributionRefDate: dateTimeSchema.nullish(),
    eventTypeId: eventTypeIdEnum,
  })
  .nullish()
  .transform((betMetricTransformSchema) => {
    if (!betMetricTransformSchema) {
      return undefined;
    }
    const { values, eventTypeId } = betMetricTransformSchema;
    const eventType = EVENT_TYPE_ID_VARIANTS[eventTypeId];
    const outcomeCount = getOutcomeCount(eventType);
    const currentOdds: (string | undefined)[] = Array.from({ length: outcomeCount }, () => undefined);
    const currentStartOdds: (string | undefined)[] = Array.from({ length: outcomeCount }, () => undefined);
    const currentFavorites: (string | undefined)[] = Array.from({ length: outcomeCount }, () => undefined);
    const currentDistribution: (string | undefined)[] = Array.from({ length: outcomeCount }, () => undefined);
    const previousDistribution: (string | undefined)[] = Array.from({ length: outcomeCount }, () => undefined);

    // HEAD_TO_HEAD (eventTypeId 12) has the same 1X2 outcome layout
    if (eventType === 'EVENT_1X2' || eventType === 'HEAD_TO_HEAD') {
      for (const [i, value] of values.entries()) {
        if (value.odds?.odds) {
          currentOdds[i] = formatValue(value.odds.odds);
        }
        if (value.odds?.startOdds) {
          currentStartOdds[i] = formatValue(value.odds.startOdds);
        }
        if (value.odds?.favouriteOdds) {
          currentFavorites[i] = formatValue(value.odds.favouriteOdds);
        }
        if (value.distribution?.distribution) {
          currentDistribution[i] = value.distribution.distribution;
        }
        if (value.distribution?.refDistribution) {
          previousDistribution[i] = value.distribution.refDistribution;
        }
      }
    }

    const betMetrics: BetMetrics = {
      odds: {
        current: {
          value: currentOdds,
        },
        valueType: 'odds',
      },
      favouriteOdds: {
        current: {
          value: currentFavorites,
        },
        valueType: 'percentage',
      },
      startOdds: {
        current: {
          value: currentStartOdds,
        },
        valueType: 'odds',
      },
      distribution: {
        current: {
          value: currentDistribution,
        },
        previous: {
          value: previousDistribution,
        },
        valueType: 'percentage',
      },
    };

    return betMetrics;
  });

/**
 * Zod schema for complementary BetMetrics entity
 */
export const complementaryBetMetricsTransformSchema = z.array(betMetricsTransformSchema);

const providersVariants = ['BetRadar', 'SweHockey', 'Kambi'] as const;

/**
 * Zod enum - The different providers for event statistics
 */
export const providersTypeEnum = z
  .enum([...providersVariants, 'SportRadar'])
  // Map SportRadar to BetRadar for sport API
  .transform<(typeof providersVariants)[number]>((provider) => (provider === 'SportRadar' ? 'BetRadar' : provider));
export type EventIdProviders = z.infer<typeof providersTypeEnum>;

export const providersTransformSchema = z
  .array(
    z.object({
      provider: providersTypeEnum,
      id: z.string(),
    })
  )
  .optional()
  .transform((providers) => {
    const providerObject: Record<EventIdProviders, string> = {} as Record<EventIdProviders, string>;
    if (!providers) {
      return undefined;
    }
    for (const provider of providers) {
      providerObject[provider.provider] = provider.id;
    }
    return providerObject;
  });

export const tioTidningarsTipsTransformSchema = z
  .object({
    one: z.number(),
    x: z.number(),
    two: z.number(),
  })
  .nullish()
  .transform((result) => {
    if (!result) {
      return undefined;
    }
    const tioTidningarsTips: MetricsObject = {
      current: {
        value: Object.values(result).map((value) => JSON.stringify(value)),
      },
      valueType: 'count',
    };
    return tioTidningarsTips;
  });
